import { DOCUMENT } from '@angular/common';
import {
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  Placement,
  shift,
} from '@floating-ui/dom';

import { SuiTooltipComponent } from './sui-tooltip.component';
import { SUI_BOUNDARY_SELECTOR } from '@sensika/sui/shared';

/**
 * Placement vocabulary — `top | bottom | start | end` plus the alignment
 * variants. `start`/`end` are physical (LTR): start = left, end = right. RTL
 * call sites swap the value themselves.
 */
export type SuiTooltipPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'start'
  | 'start-top'
  | 'start-bottom'
  | 'end'
  | 'end-top'
  | 'end-bottom';

/** Gap between trigger and tooltip, px. */
const TOOLTIP_OFFSET = 8;

/** Keep this much space between the tooltip and the viewport/boundary edge. */
const VIEWPORT_PADDING = 8;

/** Delay before a hovered tooltip opens (ms) — avoids flicker on pass-through. */
const OPEN_DELAY = 100;

const PLACEMENT_MAP: Record<SuiTooltipPlacement, Placement> = {
  top: 'top',
  'top-start': 'top-start',
  'top-end': 'top-end',
  bottom: 'bottom',
  'bottom-start': 'bottom-start',
  'bottom-end': 'bottom-end',
  start: 'left',
  'start-top': 'left-start',
  'start-bottom': 'left-end',
  end: 'right',
  'end-top': 'right-start',
  'end-bottom': 'right-end',
};

let nextId = 0;

/**
 * `[suiTooltip]` — the sui tooltip. Renders **inline** (a sibling of the
 * trigger), so it inherits the trigger's stacking context and works on plain
 * pages *and* inside the app's `transform`-centered dialogs with no overlay,
 * no portal, and no z-index juggling. Positioned with `@floating-ui/dom`
 * (offset + flip + shift). Inverted
 * + borderless via the `--tooltip-*` tokens. Shows on hover + keyboard focus,
 * hides on leave / blur / Escape.
 *
 * ```html
 * <button [suiTooltip]="'feed.button.edit' | translate"
 *         suiTooltipPlacement="bottom">Edit</button>
 *
 * <button [suiTooltip]="rich">Saved</button>
 * <ng-template #rich><fa-icon [icon]="check" /> Saved just now</ng-template>
 * ```
 *
 * - `[suiTooltip]` — `string | TemplateRef | null`. Falsy ⇒ no tooltip.
 * - `[suiTooltipPlacement]`, `[suiTooltipDisabled]`.
 * - `[suiTooltipContainer]` — **escape hatch for clipping** (when the trigger
 *   sits inside an `overflow: hidden`/scroll ancestor that would cut the inline
 *   tooltip off). `'auto'` hoists to the nearest boundary pane
 *   (`data-sui-popover-boundary`), else `<body>`;
 *   a selector/element targets an explicit host. Omit it for the common case —
 *   inline already works in dialogs.
 * - `exportAs: 'suiTooltip'` → `close()` for the dropdown-coordination pattern
 *   (`#t="suiTooltip"`, then a dropdown's `(openChange)="$event && t.close()"`).
 */
@Directive({
  selector: '[suiTooltip]',
  standalone: true,
  exportAs: 'suiTooltip',
})
export class SuiTooltipDirective implements OnChanges, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly vcr = inject(ViewContainerRef);
  private readonly document = inject(DOCUMENT);

  /** Tooltip content. Falsy ⇒ no tooltip. */
  @Input('suiTooltip') content:
    | string
    | TemplateRef<unknown>
    | null
    | undefined = '';
  @Input('suiTooltipPlacement') placement:
    | SuiTooltipPlacement
    | SuiTooltipPlacement[] = 'top';
  @Input('suiTooltipDisabled') disabled = false;
  /** Escape hatch for clipping: `'auto'` | selector | element. Omit on plain inline use. */
  @Input('suiTooltipContainer') container?:
    | 'auto'
    | string
    | HTMLElement
    | null;

  private componentRef?: ComponentRef<SuiTooltipComponent>;
  private cleanupAutoUpdate?: () => void;
  private openTimer?: ReturnType<typeof setTimeout>;
  private readonly tooltipId = `sui-tooltip-${++nextId}`;

  get isOpen(): boolean {
    return !!this.componentRef;
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.scheduleOpen();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.close();
  }

  // focusin/out (bubble) so a focusable descendant also triggers it; opens
  // instantly on keyboard focus (no hover delay).
  @HostListener('focusin')
  onFocusIn(): void {
    this.open();
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this.close();
  }

  @HostListener('keydown.escape')
  onEscape(): void {
    this.close();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['content'] || changes['disabled']) &&
      this.isOpen &&
      (this.disabled || !this.content)
    ) {
      this.close();
    }
  }

  open(): void {
    this.clearTimer();
    if (this.isOpen || this.disabled || !this.content) return;

    // Created in the directive's own view container → the element lands as a
    // sibling of the trigger (inline, like ngb). The escape hatch then moves
    // the DOM node out; the Angular view stays owned by this VCR either way.
    const ref = this.vcr.createComponent(SuiTooltipComponent);
    this.componentRef = ref;

    if (this.content instanceof TemplateRef) {
      ref.setInput('template', this.content);
    } else {
      ref.setInput('text', this.content);
    }
    ref.setInput('id', this.tooltipId);

    const tooltipEl = ref.location.nativeElement as HTMLElement;
    const target = this.resolveTarget();
    if (target) {
      target.el.appendChild(tooltipEl);
      tooltipEl.style.position =
        target.strategy === 'fixed' ? 'fixed' : 'absolute';
    } else {
      tooltipEl.style.position = 'absolute';
    }

    ref.changeDetectorRef.detectChanges();
    this.startPositioning(tooltipEl, target?.strategy ?? 'absolute');

    this.host.nativeElement.setAttribute('aria-describedby', this.tooltipId);
  }

  close(): void {
    this.clearTimer();
    if (!this.componentRef) return;
    this.cleanupAutoUpdate?.();
    this.cleanupAutoUpdate = undefined;
    this.componentRef.destroy();
    this.componentRef = undefined;
    this.host.nativeElement.removeAttribute('aria-describedby');
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  ngOnDestroy(): void {
    this.close();
  }

  private scheduleOpen(): void {
    if (this.openTimer || this.isOpen) return;
    this.openTimer = setTimeout(() => {
      this.openTimer = undefined;
      this.open();
    }, OPEN_DELAY);
  }

  private clearTimer(): void {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = undefined;
    }
  }

  /** Keep the tooltip glued to the trigger across scroll/resize/layout shifts. */
  private startPositioning(
    tooltipEl: HTMLElement,
    strategy: 'absolute' | 'fixed',
  ): void {
    const [primary, ...fallbacks] = this.placementList();
    const reference = this.host.nativeElement;

    this.cleanupAutoUpdate = autoUpdate(reference, tooltipEl, () => {
      computePosition(reference, tooltipEl, {
        strategy,
        placement: primary,
        middleware: [
          offset(TOOLTIP_OFFSET),
          flip(fallbacks.length ? { fallbackPlacements: fallbacks } : {}),
          shift({ padding: VIEWPORT_PADDING }),
        ],
      }).then(({ x, y }) => {
        // Position via left/top so the component's scale-in `transform` is free.
        tooltipEl.style.left = `${x}px`;
        tooltipEl.style.top = `${y}px`;
      });
    });
  }

  private placementList(): Placement[] {
    const requested = Array.isArray(this.placement)
      ? this.placement
      : [this.placement];
    const mapped = requested
      .map((p) => PLACEMENT_MAP[p])
      .filter((p): p is Placement => !!p);
    return mapped.length ? mapped : ['top'];
  }

  /**
   * Resolve the escape-hatch host. `null` ⇒ inline (leave where the VCR put it).
   * `'auto'` ⇒ nearest boundary pane if inside one, else `<body>`. A selector/
   * element targets that host. `<body>` uses the `fixed` strategy; an in-page
   * element uses `absolute`.
   */
  private resolveTarget(): {
    el: HTMLElement;
    strategy: 'absolute' | 'fixed';
  } | null {
    if (!this.container) return null;

    let el: HTMLElement | null;
    if (this.container === 'auto') {
      el =
        this.host.nativeElement.closest<HTMLElement>(SUI_BOUNDARY_SELECTOR) ??
        this.document.body;
    } else if (typeof this.container === 'string') {
      el = this.document.querySelector<HTMLElement>(this.container);
    } else {
      el = this.container;
    }
    if (!el) return null;

    return { el, strategy: el === this.document.body ? 'fixed' : 'absolute' };
  }
}
